const extractErrorBag = (error) => {
  if (!error || typeof error !== "object") return null;
  if (error.errors && typeof error.errors === "object") return error.errors;
  if (error?.response?.data?.errors && typeof error.response.data.errors === "object") {
    return error.response.data.errors;
  }
  if (error?.data?.errors && typeof error.data.errors === "object") {
    return error.data.errors;
  }
  return null;
};

const normalizeErrorEntries = (errorBag) => {
  if (!errorBag) return [];
  return Object.entries(errorBag).flatMap(([key, val]) => {
    if (Array.isArray(val)) {
      return val.map((msg) => [key, String(msg)]);
    }
    if (val === null || val === undefined) return [];
    if (typeof val === "string") return [[key, val]];
    return [];
  });
};

const toFieldLabel = (field, fieldLabelMap) => {
  if (!field) return "Field";
  if (fieldLabelMap && field in fieldLabelMap) return fieldLabelMap[field];
  return String(field).replace(/_/g, " ");
};

const buildToastDescription = (entries, options) => {
  const {
    fieldLabelMap,
    nonFieldKeys = ["non_field_errors", "detail", "message"],
    limit = 5,
  } = options || {};

  const lines = [];
  entries.slice(0, limit).forEach(([field, message]) => {
    if (nonFieldKeys.includes(field)) {
      lines.push(message);
      return;
    }
    lines.push(`${toFieldLabel(field, fieldLabelMap)}: ${message}`);
  });

  if (entries.length > limit) {
    lines.push(`+${entries.length - limit} more`);
  }

  return lines.join("\n");
};

// Backend messages that are technically "the message" but carry no useful
// detail for a user — prefer a validation bag or `error` detail over these.
const GENERIC_BACKEND_MESSAGES = [
  "500 Internal Server Error. Something went wrong.",
  "422 Unprocessable Entity. Validation failed.",
];

/**
 * Extract the best human-readable message from any error shape we throw:
 *  - a plain string
 *  - the interceptor's normalized `{ customMessage }` (network / 401 / 403)
 *  - a backend body `{ message, error, errors }` (services throw `error.response.data`)
 *  - a raw axios error (`error.response.data...`)
 *
 * Falls back to `fallback` when nothing meaningful is available (e.g. a
 * production 500 that only says "Please try again later.").
 */
export const getApiErrorMessage = (
  error,
  fallback = "Something went wrong. Please try again."
) => {
  if (!error) return fallback;
  if (typeof error === "string") return error;

  // Interceptor-normalized errors (network failure, session, access denied)
  if (error.customMessage) return error.customMessage;

  // Field-level validation errors — summarize them into one readable message
  const entries = normalizeErrorEntries(extractErrorBag(error));
  if (entries.length) {
    return buildToastDescription(entries, {});
  }

  // Prefer a meaningful `message`, then the backend `error` detail
  const body = error.response?.data ?? error.data ?? error;
  const message = body?.message;
  const detail = body?.error;

  if (message && !GENERIC_BACKEND_MESSAGES.includes(message)) return message;
  if (typeof detail === "string" && detail.trim()) return detail;

  return fallback;
};

export const toastApiValidationErrors = (error, options = {}) => {
  const {
    toast,
    setErrors,
    title = "Validation Error.",
    fallbackMessage = "Please check your input.",
    autoClose = 5000,
  } = options;

  const errorBag = extractErrorBag(error);
  const entries = normalizeErrorEntries(errorBag);
  if (!entries.length) return false;

  if (typeof setErrors === "function") {
    const fieldErrors = Object.fromEntries(
      entries.map(([key, message]) => [key, message])
    );
    setErrors(fieldErrors);
  }

  if (toast && typeof toast.warning === "function") {
    const description = buildToastDescription(entries, options);
    toast.warning(title, {
      description: description || fallbackMessage,
      autoClose,
    });
  }

  return true;
};

